import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Box, Spacer } from '@chakra-ui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';

import { ChevronUpSolid } from '../../../design-system/icon';
import { Heading } from '../../../design-system/typography';
import { useLocalStorage } from '../../../lib/storage';
import { ErrorBoundary } from '../../error/error-boundary';
import { ReactComponent as AiSparkle } from './ai-sparkle.svg';
import { Dimension, ScoreBoxAccordionContent } from './scorebox-components';

type ScoreBoxAccordionProps = {
	dimensions: Dimension[];
	isLoading: boolean;
	text: string;
};

export const ScoreBoxAccordion: FC<ScoreBoxAccordionProps> = ({ dimensions, isLoading, text }) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'gratitudeGauge' });
	const [isOpen, setIsOpen] = useLocalStorage('gratitude-gauge-open:mobile', true);
	const { track } = useAnalytics();
	const matches = dimensions.filter((d) => d.matches).length;

	return (
		<ErrorBoundary>
			<Accordion
				allowToggle
				index={isOpen ? 0 : -1}
				onChange={(index) => {
					const nextIsOpen = index === 0;

					if (nextIsOpen) {
						track(AnalyticsEvent.GratitudeGauge.Opened, { type: 'accordion' });
					} else {
						track(AnalyticsEvent.GratitudeGauge.Closed, { type: 'accordion' });
					}

					setIsOpen(nextIsOpen);
				}}
			>
				<AccordionItem borderBottom="none">
					{({ isExpanded }) => (
						<>
							<AccordionButton
								bgColor="purple.400"
								color="white"
								columnGap={2}
								justifyContent="center"
								pb={{ base: 4, sm: 2 }}
								_hover={{ bgColor: 'purple.500' }}
							>
								<AiSparkle />
								<Heading>{t('heading')}</Heading>
								<Box color="white" fontSize="sm" fontWeight="medium" borderRadius="md" px={2} bg="purple.300" letterSpacing="wider">
									{matches}/{dimensions.length}
								</Box>
								<Spacer />
								<ChevronUpSolid transitionProperty="transform" transitionDuration="fast" transform={isExpanded ? 'none' : 'rotate(180deg)'} />
							</AccordionButton>

							<AccordionPanel p={3}>
								<ScoreBoxAccordionContent dimensions={dimensions} isLoading={isLoading} text={text} />
							</AccordionPanel>
						</>
					)}
				</AccordionItem>
			</Accordion>
		</ErrorBoundary>
	);
};
